/**
 * HP Flipbook — StPageFlip (canvas, CSP-safe) + kağıt dokusu + ses
 * Reis Kuyumculuk
 */
(function () {
  'use strict';

  /* ── Kağıt grain dokusu (SVG turbulence, seamless tile) ── */
  const PAPER_SVG =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="320">' +
        '<filter id="n">' +
          '<feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/>' +
          '<feColorMatrix values="0 0 0 0 0.84  0 0 0 0 0.81  0 0 0 0 0.76  0 0 0 0.5 0"/>' +
        '</filter>' +
        '<rect width="320" height="320" filter="url(#n)"/>' +
      '</svg>'
    );

  let _paperTex = null;

  function loadImg(src) {
    return new Promise((res, rej) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => res(img);
      img.onerror = () => rej(new Error('Görsel yüklenemedi: ' + src));
      img.src = src;
    });
  }

  async function getPaperTex() {
    if (_paperTex) return _paperTex;
    _paperTex = await loadImg(PAPER_SVG);
    return _paperTex;
  }

  async function bakeTexture(src, alpha) {
    try {
      const [img, tex] = await Promise.all([loadImg(src), getPaperTex()]);
      const w = img.naturalWidth  || 800;
      const h = img.naturalHeight || 1120;
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      /* kağıt grain */
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = alpha;
      const pat = ctx.createPattern(tex, 'repeat');
      ctx.fillStyle = pat;
      ctx.fillRect(0, 0, w, h);
      /* kenar gölgesi — kitap dikişi hissi */
      ctx.globalAlpha = 0.2;
      const gr = ctx.createLinearGradient(0, 0, w, 0);
      gr.addColorStop(0,    '#999');
      gr.addColorStop(0.05, 'transparent');
      gr.addColorStop(0.95, 'transparent');
      gr.addColorStop(1,    '#999');
      ctx.fillStyle = gr;
      ctx.fillRect(0, 0, w, h);
      return c.toDataURL('image/jpeg', 0.88);
    } catch (_) {
      return src; // fallback: orijinal
    }
  }

  /* ── Ses: Web Audio sentezi (CSP-safe, eval yok) ── */
  function makeSoundPlayer(volume, customUrl) {
    let ctx = null, customBuf = null, muted = false, booting = null;

    function boot() {
      if (booting) return booting;
      booting = (async () => {
        if (ctx) return;
        const AC = window.AudioContext || /** @type {any} */(window).webkitAudioContext;
        if (!AC) return;
        ctx = new AC();
        if (customUrl) {
          try {
            const ab = await (await fetch(customUrl)).arrayBuffer();
            customBuf = await ctx.decodeAudioData(ab);
          } catch (_) {}
        }
      })();
      return booting;
    }

    async function play() {
      if (muted) return;
      await boot(); // AudioContext hazır olana kadar bekle
      if (!ctx) return;
      if (ctx.state === 'suspended') await ctx.resume();
      const gain = ctx.createGain();
      gain.gain.value = volume;
      gain.connect(ctx.destination);
      if (customBuf) {
        const s = ctx.createBufferSource();
        s.buffer = customBuf; s.connect(gain); s.start();
        return;
      }
      const dur  = 0.38;
      const buf  = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        const t = i / data.length;
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 1.8) * 0.5;
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 900;
      const lp = ctx.createBiquadFilter(); lp.type = 'lowpass';  lp.frequency.value = 9000;
      src.connect(hp); hp.connect(lp); lp.connect(gain);
      src.start();
    }

    return {
      boot, play,
      setMuted: (m) => { muted = !!m; },
      isMuted:  ()  => muted,
    };
  }

  /* ── StPageFlip loader ── */
  const _scripts = {};
  function loadScript(src) {
    if (_scripts[src]) return _scripts[src];
    _scripts[src] = new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = res; s.onerror = () => rej(new Error('Script yüklenemedi: ' + src));
      document.head.appendChild(s);
    });
    return _scripts[src];
  }

  /* ── PDF.js ── */
  async function ensurePdf(pdfSrc, workerSrc) {
    if (window.pdfjsLib) return;
    await loadScript(pdfSrc);
    if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
  }

  /* Sayfalar, sahnede kaplayacakları gerçek piksel yüksekliğine göre render edilir
     (retina dahil, en fazla 2x). Sabit ölçek küçük sayfalı PDF'lerde (A5/A6) bulanık çıkıyordu. */
  async function openPdf(url, pdfSrc, workerSrc) {
    await ensurePdf(pdfSrc, workerSrc);
    return window.pdfjsLib.getDocument(url).promise;
  }

  /* Sayfaları sırayla render eder; her sayfa hazır olunca onPage(index, dataUrl) çağrılır.
     Kitap ilk birkaç sayfayla açılır, kalanı arka planda dolar. shouldStop(): sekme değiştiyse bırak. */
  async function renderPdfPages(pdf, targetHeightPx, quality, from, to, onPage, shouldStop) {
    const first = await pdf.getPage(1);
    const base  = first.getViewport({ scale: 1 });
    const scale = Math.max(0.5, Math.min(6, targetHeightPx / base.height));
    for (let i = from; i <= to; i++) {
      if (shouldStop && shouldStop()) return false;
      const pg = await pdf.getPage(i);
      const vp = pg.getViewport({ scale });
      const c  = document.createElement('canvas');
      c.width = vp.width; c.height = vp.height;
      await pg.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
      onPage(i - 1, c.toDataURL('image/jpeg', quality));
    }
    return true;
  }

  /* Henüz render edilmemiş sayfalar için hafif yer tutucu (aynı en-boy oranı) */
  function placeholderPage(w, h, bg) {
    const svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 " + w + " " + h + "'><rect width='100%' height='100%' fill='" + bg + "'/></svg>";
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  /* ── Hotspot overlay ── */
  function buildHotspotOverlay(stage, hotspots) {
    const el = document.createElement('div');
    el.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:4;';
    stage.appendChild(el);

    let visible = true, curPage = 0, single = false;

    function draw() {
      el.innerHTML = '';
      if (!visible || !hotspots.length) return;
      const canvas = stage.querySelector('canvas');
      if (!canvas) return;
      const sr = stage.getBoundingClientRect();
      const cr = canvas.getBoundingClientRect();
      const ox = cr.left - sr.left, oy = cr.top - sr.top;
      const cw = cr.width, ch = cr.height;

      function addPage(pageNo, px, pw) {
        hotspots.filter(h => Number(h.page) === pageNo).forEach(h => {
          const a = document.createElement('a');
          a.className = 'hp-flipbook__hotspot';
          a.href = h.url || '#';
          if (h.target) a.target = h.target;
          a.style.cssText = `
            position:absolute;pointer-events:auto;
            left:${px + (h.x||0)/100*pw}px;
            top:${oy + (h.y||0)/100*ch}px;
            width:${(h.w||10)/100*pw}px;
            height:${(h.h||10)/100*ch}px;
          `;
          if (h.label) {
            const s = document.createElement('span');
            s.className = 'hp-flipbook__hotspot-label';
            s.textContent = h.label;
            a.appendChild(s);
          } else {
            a.appendChild(Object.assign(document.createElement('span'), { className: 'hp-flipbook__hotspot-pulse' }));
          }
          let dx = 0, dy = 0;
          a.addEventListener('pointerdown', e => { dx = e.clientX; dy = e.clientY; });
          a.addEventListener('click', e => { if (Math.abs(e.clientX-dx)>6 || Math.abs(e.clientY-dy)>6) e.preventDefault(); });
          el.appendChild(a);
        });
      }

      if (single) {
        addPage(curPage + 1, ox, cw);
      } else {
        const hw = cw / 2;
        addPage(curPage + 1,      ox,      hw);
        addPage(curPage + 2,      ox + hw, hw);
      }
    }

    return {
      setPage: (idx, isSingle) => { curPage = idx; single = isSingle; draw(); },
      hide: () => { visible = false; el.innerHTML = ''; },
      show: () => { visible = true; draw(); },
      refresh: draw,
    };
  }

  /* ── Mobil slider: StPageFlip yerine basit yatay slider (kitap mantığı yok) ── */
  function initMobileSlider({ root, stage, mount, loader, pages, restJob, stale, btnPrev, btnNext, counter, btnSound, soundOn, soundUrl, soundVol }) {
    /* Desktop'taki StPageFlip mount'unu gizle; slider DOM'u stage'e direkt eklenir */
    mount.style.display = 'none';

    const slider = document.createElement('div');
    slider.className = 'hp-flipbook__slider';
    const track = document.createElement('div');
    track.className = 'hp-flipbook__slider-track';

    pages.forEach((src, i) => {
      const slide = document.createElement('div');
      slide.className = 'hp-flipbook__slide';
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.draggable = false;
      img.loading = i < 2 ? 'eager' : 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);
    });
    slider.appendChild(track);
    stage.appendChild(slider);

    /* Kalan sayfalar hazır oldukça yer tutucuları değiştir */
    if (restJob) {
      const imgs = track.querySelectorAll('img');
      setTimeout(() => restJob(() => {
        if (stale && stale()) return;
        imgs.forEach((img, i) => { if (pages[i] && img.src !== pages[i] && !pages[i].startsWith('data:image/svg')) img.src = pages[i]; });
      }).catch(() => {}), 200);
    }

    const sound = soundOn
      ? makeSoundPlayer(soundVol, soundUrl)
      : { boot: () => {}, play: () => {}, setMuted: () => {}, isMuted: () => true };

    let idx = 0;
    const total = pages.length;

    function update() {
      track.style.transform = 'translateX(' + (-idx * 100) + '%)';
      if (counter) counter.textContent = (idx + 1) + ' / ' + total;
      if (btnPrev) btnPrev.disabled = idx <= 0;
      if (btnNext) btnNext.disabled = idx >= total - 1;
    }

    function go(delta) {
      const next = Math.max(0, Math.min(total - 1, idx + delta));
      if (next === idx) return;
      idx = next;
      sound.play();
      update();
    }

    if (btnPrev) btnPrev.addEventListener('click', () => go(-1));
    if (btnNext) btnNext.addEventListener('click', () => go(1));

    /* Swipe: yatay hareket eşiği >40px ve yatay >dikey ise sayfa atla; dikey scroll bloklanmaz. */
    let tx = null, ty = null, swiping = false;
    slider.addEventListener('touchstart', (e) => {
      tx = e.touches[0].clientX;
      ty = e.touches[0].clientY;
      swiping = false;
    }, { passive: true });
    slider.addEventListener('touchmove', (e) => {
      if (tx === null) return;
      const dx = e.touches[0].clientX - tx;
      const dy = e.touches[0].clientY - ty;
      if (!swiping && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) swiping = true;
    }, { passive: true });
    slider.addEventListener('touchend', (e) => {
      if (tx === null) return;
      const wasSwiping = swiping;
      const dx = e.changedTouches[0].clientX - tx;
      tx = ty = null; swiping = false;
      if (wasSwiping && Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    });

    /* Ses butonu */
    if (btnSound) {
      const icOn  = btnSound.querySelector('[data-icon-sound-on]');
      const icOff = btnSound.querySelector('[data-icon-sound-off]');
      const upd = () => {
        if (icOn)  icOn.style.display  = sound.isMuted() ? 'none' : '';
        if (icOff) icOff.style.display = sound.isMuted() ? '' : 'none';
      };
      upd();
      btnSound.addEventListener('click', () => { sound.setMuted(!sound.isMuted()); upd(); });
    }

    /* İlk dokunuşta AudioContext'i hazırla (tarayıcı politikası) */
    const bootSound = () => { sound.boot(); root.removeEventListener('click', bootSound); };
    root.addEventListener('click', bootSound);

    /* Loader: ilk görsel yüklenince kapat */
    const firstImg = track.querySelector('img');
    const hideLoader = () => { if (loader) loader.classList.add('is-hidden'); };
    if (firstImg && !firstImg.complete) firstImg.addEventListener('load', hideLoader, { once: true });
    else hideLoader();

    update();
  }

  /* ── Main init ── */
  async function init(root) {
    const stage  = root.querySelector('[data-hp-flipbook-stage]');
    const mount  = root.querySelector('[data-hp-flipbook-mount]');
    const loader = root.querySelector('[data-hp-flipbook-loader]');
    if (!stage || !mount) return;

    /* Her (yeniden) başlatma bir nesil; sekme değişince eski neslin arka plan işleri iptal olur. */
    const gen = (root._hpGen = (root._hpGen || 0) + 1);
    const stale = () => root._hpGen !== gen;
    let restJob = null; /* kalan sayfaları arka planda render eden iş: (onChunk) => Promise<boolean> */

    const sourceType   = root.dataset.sourceType   || 'images';
    const pdfUrl       = root.dataset.pdfUrl        || '';
    const mobileSingle = root.dataset.mobileSingle  !== 'false';
    const useTexture   = root.dataset.usePaperTexture !== 'false';
    const texAlpha     = parseFloat(root.dataset.textureAlpha   || '0.18');
    const soundOn      = root.dataset.soundEnabled  !== 'false';
    const soundUrl     = root.dataset.soundUrl      || '';
    const soundVol     = parseFloat(root.dataset.soundVolume    || '0.5');
    const flipDur      = parseInt(root.dataset.flipDuration     || '900', 10);
    const stSrc        = root.dataset.stpageflipSrc;
    const pdfSrc       = root.dataset.pdfjsSrc;
    const workerSrc    = root.dataset.pdfjsWorkerSrc;

    let hotspots = [];
    try { hotspots = JSON.parse(root.dataset.hpFlipbookHotspots || '[]'); } catch (_) {}

    /* 1. Sayfa görselleri */
    let rawImgs = [];
    if (sourceType === 'pdf') {
      if (!pdfUrl) { if (loader) loader.textContent = 'PDF URL eksik.'; return; }
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const stageH = (stage && stage.getBoundingClientRect().height) || 800;
      const targetH = Math.min(2400, Math.round(Math.max(stageH, 700) * dpr));
      try {
        const pdf = await openPdf(pdfUrl, pdfSrc, workerSrc);
        if (stale()) return;
        const n = pdf.numPages;
        const firstBatch = Math.min(4, n);
        const base = (await pdf.getPage(1)).getViewport({ scale: 1 });
        root.dataset.pageRatio = (base.height / base.width).toFixed(4);
        const ph = placeholderPage(Math.round(base.width), Math.round(base.height), '#f3f1ec');
        rawImgs = new Array(n).fill(ph);
        /* İlk 4 sayfa hemen: kitap bunlarla açılır */
        const ok = await renderPdfPages(pdf, targetH, 0.86, 1, firstBatch, (i, src) => { rawImgs[i] = src; }, stale);
        if (!ok || stale()) return;
        /* Kalan sayfalar kitap açıldıktan sonra arka planda; her 6 sayfada bir kitap güncellenir */
        if (n > firstBatch) {
          restJob = (onChunk) => renderPdfPages(pdf, targetH, 0.86, firstBatch + 1, n, (i, src) => {
            rawImgs[i] = src;
            if ((i + 1 - firstBatch) % 6 === 0 || i === n - 1) onChunk(i);
          }, stale);
        }
      }
      catch (e) { console.error('[hp-flipbook] PDF:', e); if (loader) loader.textContent = 'PDF yüklenemedi.'; return; }
    } else {
      rawImgs = Array.from(root.querySelectorAll('[data-hp-flipbook-image]'))
        .map(el => el.dataset.src).filter(Boolean);
    }
    if (!rawImgs.length) { if (loader) loader.textContent = 'Sayfa bulunamadı.'; return; }

    /* Mobilde (≤999px) StPageFlip'i hiç yükleme: kitap mantığı yerine basit slider'a geç. */
    if (window.matchMedia('(max-width: 999px)').matches && mobileSingle) {
      if (root.dataset.pageRatio) root.style.setProperty('--hp-fb-aspect', root.dataset.pageRatio);
      initMobileSlider({
        root, stage, mount, loader,
        pages: rawImgs, restJob, stale,
        btnPrev: root.querySelector('[data-hp-flipbook-prev]'),
        btnNext: root.querySelector('[data-hp-flipbook-next]'),
        counter: root.querySelector('[data-hp-flipbook-counter]'),
        btnSound: root.querySelector('[data-hp-flipbook-sound]'),
        soundOn, soundUrl, soundVol,
      });
      return;
    }

    /* 2. Kağıt dokusu bake */
    const pages = useTexture
      ? await Promise.all(rawImgs.map(s => bakeTexture(s, texAlpha)))
      : rawImgs;

    /* 3. StPageFlip yükle */
    try { await loadScript(stSrc); } catch (e) {
      console.error('[hp-flipbook] StPageFlip:', e);
      if (loader) loader.textContent = 'Kitap motoru yüklenemedi.'; return;
    }
    if (!window.St || !window.St.PageFlip) {
      if (loader) loader.textContent = 'Kitap motoru başlatılamadı.'; return;
    }

    /* 4. Boyut */
    const isMobile   = window.matchMedia('(max-width: 768px)').matches;
    const forceSingle = isMobile && mobileSingle;
    /* Sayfa oranı kaynaktan (PDF sayfa boyutu / ilk görsel); sahne de aynı orana çekilir ki boşluk/basıklık olmasın */
    let ratio = parseFloat(root.dataset.pageRatio || '') || 0;
    if (!ratio) {
      try {
        const im = await loadImg(pages[0]);
        if (im.naturalWidth && im.naturalHeight) ratio = im.naturalHeight / im.naturalWidth;
      } catch (_) {}
    }
    if (!ratio || ratio < 0.5 || ratio > 2.5) ratio = 1.4;
    root.style.setProperty('--hp-fb-aspect', ratio.toFixed(3));
    const stageW = stage.clientWidth || 800;
    const pageW  = forceSingle ? stageW : Math.floor(stageW / 2);
    const pageH  = Math.floor(pageW * ratio);

    const pf = new window.St.PageFlip(mount, {
      width: pageW, height: pageH,
      size: 'stretch',
      minWidth: 180, maxWidth: 1200,
      minHeight: 250, maxHeight: 1700,
      maxShadowOpacity: 0.6,
      showCover: true,
      mobileScrollSupport: true,
      usePortrait: forceSingle,
      drawShadow: true,
      flippingTime: flipDur,
      useMouseEvents: true,
      autoSize: true,
    });

    pf.loadFromImages(pages);

    /* 5. Ses */
    const sound = soundOn ? makeSoundPlayer(soundVol, soundUrl) : { boot:()=>{}, play:()=>{}, setMuted:()=>{}, isMuted:()=>true };

    /* 6. Kapak görsel overlay (tek sayfa modunda tam boyut) */
    const cvDiv = document.createElement('div');
    cvDiv.className = 'hp-flipbook__cover-img';
    const cvImg = document.createElement('img');
    cvImg.alt = '';
    cvDiv.appendChild(cvImg);
    stage.appendChild(cvDiv);

    function showCoverImg(src) {
      cvImg.src = src;
      cvDiv.style.display = 'block';
      mount.style.opacity = '0';
      pf.getSettings().showPageCorners = false;
    }
    function hideCoverImg() {
      cvDiv.style.display = 'none';
      mount.style.opacity = '';
      pf.getSettings().showPageCorners = true;
    }

    /* 7. Hotspot overlay */
    const overlay = hotspots.length ? buildHotspotOverlay(stage, hotspots) : null;

    /* 7. Butonlar + counter */
    const btnPrev  = root.querySelector('[data-hp-flipbook-prev]');
    const btnNext  = root.querySelector('[data-hp-flipbook-next]');
    const counter  = root.querySelector('[data-hp-flipbook-counter]');
const btnSound = root.querySelector('[data-hp-flipbook-sound]');
    const icOn     = btnSound && btnSound.querySelector('[data-icon-sound-on]');
    const icOff    = btnSound && btnSound.querySelector('[data-icon-sound-off]');

    function formatCounter(idx, total) {
      const isSpread = !forceSingle && idx > 0 && idx < total - 1;
      if (isSpread) {
        const right = Math.min(idx + 2, total);
        return (idx + 1) + '-' + right + ' / ' + total;
      }
      return (idx + 1) + ' / ' + total;
    }

    function sync() {
      const idx   = pf.getCurrentPageIndex();
      const total = pf.getPageCount();
      if (counter) counter.textContent = formatCounter(idx, total);
      if (btnPrev) btnPrev.disabled = idx <= 0;
      if (btnNext) btnNext.disabled = idx >= total - 1;
      if (overlay) overlay.setPage(idx, forceSingle);
      // Kapak / arka kapak → tek sayfa modu
      const isSingle = !forceSingle && (idx === 0 || idx === total - 1);
      root.classList.toggle('is-cover', isSingle);
      if (isSingle) {
        showCoverImg(idx === 0 ? pages[0] : pages[pages.length - 1]);
        setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
      } else {
        hideCoverImg();
      }
    }

    pf.on('flip', () => { sync(); });

    // changeState: 'user_fold' / 'fold_corner' / 'flipping' → çevirme başladı, 'read' → bitti (ses burada)
    let _wasFlipping = false;
    let _pageBeforeFlip = -1;
    pf.on('changeState', (e) => {
      const state = e.data;
      if ((state === 'user_fold' || state === 'fold_corner' || state === 'flipping') && !_wasFlipping) {
        _wasFlipping = true;
        _pageBeforeFlip = pf.getCurrentPageIndex();
        // Buton yoluyla geldiyse zaten temizlendi; sürükleme yoluyla geldiyse burada yap
        if (root.classList.contains('is-cover')) {
          hideCoverImg();
          root.classList.remove('is-cover');
          window.dispatchEvent(new Event('resize'));
        }
        if (overlay) overlay.hide();
      } else if (state === 'read') {
        if (_wasFlipping && pf.getCurrentPageIndex() !== _pageBeforeFlip) sound.play();
        _wasFlipping = false;
        _pageBeforeFlip = -1;
        if (overlay) overlay.show();
        sync();
      }
    });

    pf.on('init', () => {
      if (loader) loader.classList.add('is-hidden');
      // Kapak sayfasını göster (showCover ile ilk sayfa tek kapak olarak görünür)
      pf.turnToPage(0);
      sync();

      /* Kalan sayfalar arka planda render edilir; her parça hazır olunca kitap yerinde güncellenir */
      if (restJob) {
        setTimeout(() => restJob(async () => {
          if (stale()) return;
          const cur = useTexture
            ? await Promise.all(rawImgs.map(src => src.startsWith('data:image/svg') ? src : bakeTexture(src, texAlpha)))
            : rawImgs.slice();
          if (stale()) return;
          pages.splice(0, pages.length, ...cur);
          pf.updateFromImages(cur);
          requestAnimationFrame(refit);
          sync();
        }).catch(e => console.warn('[hp-flipbook] pages:', e)), 300);
      }
    });

    function flipWithCoverTransition(dir) {
      if (root.classList.contains('is-cover')) {
        // Önce stage'i landscape'e al, 2 frame bekle → layout settle → sonra flip başlat
        hideCoverImg();
        root.classList.remove('is-cover');
        window.dispatchEvent(new Event('resize'));
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (dir === 'next') pf.flipNext(); else pf.flipPrev();
        }));
      } else {
        if (dir === 'next') pf.flipNext(); else pf.flipPrev();
      }
    }

    if (btnPrev) btnPrev.addEventListener('click', () => flipWithCoverTransition('prev'));
    if (btnNext) btnNext.addEventListener('click', () => flipWithCoverTransition('next'));

    /* İlk tıklamada AudioContext'i ön-başlat (tarayıcı politikası) */
    const bootSound = () => { sound.boot(); root.removeEventListener('click', bootSound); };
    root.addEventListener('click', bootSound);

    if (btnSound) {
      const upd = () => { if (icOn) icOn.style.display = sound.isMuted()?'none':''; if (icOff) icOff.style.display = sound.isMuted()?'':'none'; };
      upd();
      btnSound.addEventListener('click', () => { sound.setMuted(!sound.isMuted()); upd(); });
    }

    let rsz;
    window.addEventListener('resize', () => { clearTimeout(rsz); rsz = setTimeout(() => overlay && overlay.refresh(), 200); });

    /* Sahne boyutu pencere olmadan da değişebilir (kapak ↔ çift sayfa, editör, font yüklenmesi):
       StPageFlip sadece window resize dinliyor; sahneyi izleyip canvas'ı yeniden ölçekliyoruz. */
    /* Canvas'ı sahnenin güncel boyutuna yeniden ölçekle (backing store + geometri) */
    function refit() {
      try { const ui = pf.getUI && pf.getUI(); if (ui && ui.update) ui.update(); else pf.update(); } catch (_) {}
    }
    if (window.ResizeObserver) {
      let roT;
      const ro = new ResizeObserver(() => {
        clearTimeout(roT);
        roT = setTimeout(() => { if (!stale()) refit(); }, 60);
      });
      ro.observe(stage);
      root._hpRo = ro;
    }

    root._hpPf = pf;
  }

  /* Sekme değişimi: eski kitabı yık, temiz markup'ı geri koy, yeni PDF ile yeniden başlat */
  function switchBook(root, pdfUrl) {
    if (root._hpPf && root._hpPf.destroy) { try { root._hpPf.destroy(); } catch (_) {} }
    if (root._hpRo) { try { root._hpRo.disconnect(); } catch (_) {} root._hpRo = null; }
    root._hpPf = null;
    delete root.dataset.pageRatio;
    root.innerHTML = root._hpPristine;
    root.dataset.sourceType = 'pdf';
    root.dataset.pdfUrl = pdfUrl;
    root.classList.remove('is-cover');
    init(root).catch(e => console.error('[hp-flipbook]', e));
  }

  const TAB_KEY = 'hpFlipbookTab';
  function readStoredTab() { try { return localStorage.getItem(TAB_KEY) || ''; } catch (_) { return ''; } }
  function storeTab(v)     { try { localStorage.setItem(TAB_KEY, v); } catch (_) {} }

  /* Sekme seçimi hatırlanır: sayfa yenilenince aynı dil (sekme + kitap) açılır. */
  function wireTabs(root) {
    const wrap = root.closest('[data-hp-flipbook-wrap]');
    if (!wrap || wrap.dataset.hpTabsWired) return false;
    wrap.dataset.hpTabsWired = '1';
    const tabs = Array.from(wrap.querySelectorAll('[data-hp-flipbook-tab]'));
    const dl   = wrap.querySelector('[data-hp-flipbook-download]');
    if (!tabs.length) return false;

    function activate(tab, opts) {
      tabs.forEach(t => { t.classList.toggle('is-active', t === tab); t.setAttribute('aria-selected', t === tab ? 'true' : 'false'); });
      if (dl && tab.dataset.downloadUrl) dl.href = tab.dataset.downloadUrl;
      storeTab(tab.dataset.pdfUrl || '');
      if (opts && opts.silent) { root.dataset.pdfUrl = tab.dataset.pdfUrl || ''; return; }
      switchBook(root, tab.dataset.pdfUrl || '');
      if (!(opts && opts.noScroll)) root.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    tabs.forEach(tab => tab.addEventListener('click', () => {
      if (!tab.classList.contains('is-active')) activate(tab);
    }));

    /* Yüklemede: hatırlanan sekme sunucunun seçtiğinden farklıysa ona geç (kitap henüz başlamadı → sessiz) */
    const stored = readStoredTab();
    const want = stored && tabs.find(t => t.dataset.pdfUrl === stored);
    if (want && !want.classList.contains('is-active')) { activate(want, { silent: true }); return true; }
    return false;
  }

  function bootAll() {
    document.querySelectorAll('[data-hp-flipbook]').forEach(root => {
      if (root.dataset.hpFlipbookBooted) return;
      root.dataset.hpFlipbookBooted = '1';
      if (!root._hpPristine) root._hpPristine = root.innerHTML;
      wireTabs(root); /* hatırlanan sekme varsa root.dataset.pdfUrl'i günceller */
      init(root).catch(e => console.error('[hp-flipbook]', e));
    });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', bootAll)
    : bootAll();

  document.addEventListener('shopify:section:load', e => {
    if (e.target?.querySelector('[data-hp-flipbook]')) bootAll();
  });
})();
